import { Property } from "types/landlord/address";
import { useMemo } from "react";
import { GeneralSection } from "./GeneralSection";
import { RentalInfoSection } from "../../../shared/app/RentalInfoSection";
import { PlanSection } from "./PlanSection";
import { PropertyKind } from "shared/types";
import { ScreeningSettingsSection } from "./ScreeningSettingsSection";
import { AmenitiesSection } from "./AmenitiesSection";
import { PhotosSection } from "./PhotosSection";
import { BrandingSection } from "./BrandingSection";
import { Show } from "shared/components";
import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { PropertyGroup } from "types/property-group";
import { useAuthContext } from "providers/Auth";
import { useDebouncedValue } from "shared/hooks";
import { ListingSection } from "views/properties-id-units-id/components/ListingSection";

interface PropertyDetailsProps {
  property: Property;
}

export const PropertyDetails = ({ property }: PropertyDetailsProps) => {
  const { type } = useAuthContext();

  const amenities =
    property.kind === PropertyKind.SingleFamily && property.units[0]
      ? property.units[0].amenities
      : property.amenities;

  const singleFamilyId =
    property.kind === PropertyKind.SingleFamily && property.units[0]
      ? property.units[0].id
      : undefined;

  const rawQuery = useMemo(
    () => ({
      owner: property.owner ?? "",
      city: property.address.city,
      state_or_province: property.address.state_or_province,
      postal_code: property.address.postal_code
    }),
    [
      property.owner,
      property.address.city,
      property.address.state_or_province,
      property.address.postal_code
    ]
  );

  const [debouncedQuery] = useDebouncedValue(rawQuery, 500);

  const { data: propertyGroup } = useQuery({
    queryKey: ["property-groups-by-address", debouncedQuery],
    queryFn: () =>
      instance
        .get("/portal/v1/property_groups/find", {
          params: debouncedQuery
        })
        .then((res) => {
          return res.data as PropertyGroup;
        }),
    enabled: type.isBoomScreen
  });

  return (
    <div>
      <div className="space-y-4 max-w-[822px] w-full">
        {property.kind === PropertyKind.SingleFamily &&
        property.units.length > 0 ? (
          <ListingSection
            propertyKind={property.kind}
            propertyId={property.id}
            unit={property.units[0]}
          />
        ) : null}
        <Show if={type.isBoomScreen}>
          <ScreeningSettingsSection
            hasPropertyGroup={Boolean(propertyGroup)}
            propertyId={property.id}
            verificationTemplateId={property.verification_template?.id}
            usePropertyGroupVerificationTemplate={Boolean(
              property.use_property_group_verification_template
            )}
          />
        </Show>
        <GeneralSection property={property} />
        {property.kind === PropertyKind.SingleFamily &&
          property.units.length > 0 && (
            <>
              <RentalInfoSection
                propertyId={property.id}
                unit={property.units[0]}
              />
              <PlanSection
                propertyId={property.id}
                unitId={property.units[0].id}
                plan={property.units[0].plan}
              />
            </>
          )}

        <Show
          if={
            (property.kind &&
              [PropertyKind.SingleFamily, PropertyKind.MultiFamily].includes(
                property.kind
              )) ||
            type.isPmsListingSyncEnabled
          }
        >
          <AmenitiesSection
            amenities={amenities || []}
            propertyId={property.id}
            petPolicy={property.pet_policy}
            propertyKind={property.kind}
          />
          <PhotosSection
            propertyId={property.id}
            propertyMedia={property.media}
            singleFamilyId={singleFamilyId}
          />
          <BrandingSection property={property} />
        </Show>
      </div>
    </div>
  );
};
